import React, { useState, useEffect, useContext, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { useProfile } from "../../hooks/useProfile";
import { apiFetch } from "../../context/AuthContext";
import AlertModal from "../../components/AlertModal";
import Mo3tiButton from "./components/layout/Mo3tiButton";

import translations from "./constants/translations";

// Hooks
import useResponsive from "./hooks/useResponsive";
import usePermissions from "./hooks/usePermissions";
import useVisibilityRules from "./hooks/useVisibilityRules";
import usePayrollData from "./hooks/usePayrollData";
import usePayrollFilters from "./hooks/usePayrollFilters";
import useAllowanceDeductionImport from "./hooks/useAllowanceDeductionImport";
import usePayrollExport from "./hooks/usePayrollExport";

// Layout & sections
import PayrollHeader from "./components/layout/PayrollHeader";
import ActionButtons from "./components/layout/ActionButtons";
import PayrollStatsGrid from "./components/stats/PayrollStatsGrid";
import PayrollFilters from "./components/filters/PayrollFilters";
import PayrollTable from "./components/table/PayrollTable";

// Modals
import ProcessPayrollModal from "./components/modals/ProcessPayrollModal/ProcessPayrollModal";
import BulkUnpayModal from "./components/modals/BulkUnpayModal/BulkUnpayModal";
import MedicalInsuranceModal from "./components/modals/MedicalInsuranceModal/MedicalInsuranceModal";
import DeductionsModal from "./components/modals/DeductionsModal/DeductionsModal";
import AllowancesModal from "./components/modals/AllowancesModal/AllowancesModal";
import EmployeeDetailsModal from "./components/modals/EmployeeDetailsModal/EmployeeDetailsModal";
import ExcelImportModal from "./components/modals/ExcelImportModal/ExcelImportModal";
import BenefitsModal from "./components/modals/BenefitsModal";
import { SkeletonStyles, SkeletonBox, LoadingPill, SkeletonStatCard } from "../../components/SkeletonLoader";

import * as XLSX from "xlsx";
import templateFile from "../../Documents/payroll_template.xlsx";
import { getCardStyle, getTopBarButtonStyle } from "../(Admin)Attendance/styles/attendanceStyles";
import {
  CreditCard,
  Upload,
  Download,
  FileText,
  Heart,
  RotateCcw,
} from "lucide-react";

const ProfessionalPayrollSystem = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();
  const t = (key) => translations[language][key] || key;
  const isRTL = language === "ar";

  const itemsPerPage = 10;
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const clientName = profile?.clientName || localStorage.getItem("clientName");
  const workingCompany =
    profile?.workingCompany || localStorage.getItem("workingCompany");

  const currentTheme = {
    colors: {
      primary: theme === "dark" ? "#2e7cba" : "#2e7cba",
      success: "#10b981",
      danger: "#ef4444",
      warning: "#f59e0b",
      info: "#2e7cba",
      textPrimary: theme === "dark" ? "#ffffff" : "#1a1a1a",
      textSecondary:
        theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
      textMuted:
        theme === "dark" ? "rgba(255, 255, 255, 1)" : "rgba(0,0,0,0.5)",
      cardBackground:
        theme === "dark" ? "rgba(30, 41, 59, 1.6)" : "rgba(255, 255, 255, 0.9)",
      inputBackground:
        theme === "dark"
          ? "rgba(30, 41, 59, 2.8)"
          : "rgba(255, 255, 255, 0.95)",
      border: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      borderLight:
        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
    },
    shadows: {
      card:
        theme === "dark"
          ? "0 8px 32px rgba(0,0,0,0.3)"
          : "0 8px 32px rgba(0,0,0,0.1)",
    },
  };

  // ───────────────────────── Hooks ─────────────────────────
  const { screenSize, spacing, fonts, container, gridCols } = useResponsive();
  const { modulePermissions, loadingPermissions } = usePermissions({ profile });
  const { visibilityRules, fetchVisibilityRules } = useVisibilityRules();

  // AlertModal state
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
    showCancel: false,
  });
  const showAlert = (title, message, variant = "info", showCancel = false) => {
    setAlertModal({ open: true, title, message, variant, showCancel });
  };
  const closeAlert = () => {
    setAlertModal({ ...alertModal, open: false });
  };

  // ─── STEP 1: Single filter instance (starts with empty data) ───
  // selectedMonth lives here and is the source of truth for the API call.
  const filters = usePayrollFilters({
    t,
    currentTheme,
    payrollData: [], // placeholder — real data injected below via setPayrollData pattern
  });

  // ─── Notification deep-link (?employee=&year=&month=) ───
  // Seed the search box with the employee's infinityCode and jump to the
  // payroll period the adjustment belongs to.
  const [searchParams] = useSearchParams();
  const deepLinkConsumedRef = useRef(false);
  useEffect(() => {
    if (deepLinkConsumedRef.current) return;
    const employee = searchParams.get("employee");
    const year = Number(searchParams.get("year"));
    const month = Number(searchParams.get("month"));
    if (!employee && !year) return;
    deepLinkConsumedRef.current = true;
    if (employee) filters.setSearchTerm(employee);
    if (year && month) filters.setSelectedMonth({ year, month });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ─── STEP 2: Fetch payroll data using selectedMonth from the filter instance ───
  const payrollDataHook = usePayrollData({
    selectedMonth: filters.selectedMonth, // ✅ correct — same instance the UI updates
    statusFilter: filters.statusFilter,
    selectedDepartment: filters.selectedDepartment,
    selectedCompany: filters.selectedCompany,
    visibilityRules,
    fetchVisibilityRules,
    profile,
    clientName,
    language,
  });

  // ─── STEP 3: Re-create filter computations with real data ───
  // We call usePayrollFilters a second time but ONLY to get the derived
  // values (filteredPayrollData, totals, salaryStats). All state setters
  // come from `filters` above — we never use the setters from this instance.
  const computed = usePayrollFilters({
    t,
    currentTheme,
    payrollData: payrollDataHook.payrollData, // real data from API
    // Mirror every filter state value so the filtering logic matches:
    _overrideState: {
      searchTerm: filters.searchTerm,
      statusFilter: filters.statusFilter,
      selectedMonth: filters.selectedMonth,
      selectedDepartment: filters.selectedDepartment,
      selectedCompany: filters.selectedCompany,
    },
  });

  // Because usePayrollFilters doesn't support _overrideState yet, we derive
  // filteredPayrollData manually here using the live filter values so there
  // is a single, correct source of truth with zero hook-order issues.
  const filteredPayrollData = React.useMemo(() => {
    return (payrollDataHook.payrollData || []).filter((payroll) => {
      const matchesSearch =
        payroll.employeeName
          ?.toLowerCase()
          .includes(filters.searchTerm.toLowerCase()) ||
        payroll.employeeId
          ?.toLowerCase()
          .includes(filters.searchTerm.toLowerCase()) ||
        // Raw infinityCode match — used by notification deep-links
        // (?employee=<infinityCode>) which seed the search box with the code.
        String(payroll.infinityCode || "").includes(filters.searchTerm);

      const matchesStatus =
        filters.statusFilter === "All" ||
        payroll.status === filters.statusFilter;

      const matchesDepartment =
        filters.selectedDepartment === "All" ||
        payroll.department === filters.selectedDepartment;

      const matchesCompany =
        filters.selectedCompany === "All" ||
        (payroll.workingCompany || "") === filters.selectedCompany;

      const fallbackDate = payroll.paidDate || payroll.payrollStartDate || null;
      const rowYear = Number(
        payroll.year ||
        (fallbackDate ? new Date(fallbackDate).getFullYear() : 0),
      );
      const rowMonth = Number(
        payroll.month ||
        (fallbackDate ? new Date(fallbackDate).getMonth() + 1 : 0),
      );

      const selectedYear = Number(filters.selectedMonth?.year || 0);
      const selectedMonthValue = Number(filters.selectedMonth?.month || 0);

      const matchesYear = !selectedYear || !rowYear || rowYear === selectedYear;
      const matchesMonth =
        !selectedMonthValue || !rowMonth || rowMonth === selectedMonthValue;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesDepartment &&
        matchesCompany &&
        matchesYear &&
        matchesMonth
      );
    });
  }, [
    payrollDataHook.payrollData,
    filters.searchTerm,
    filters.statusFilter,
    filters.selectedDepartment,
    filters.selectedCompany,
    filters.selectedMonth,
  ]);

  // Derived totals from filteredPayrollData
  const totalGrossSalary = React.useMemo(
    () => filteredPayrollData.reduce((sum, e) => sum + (e.grossSalary || 0), 0),
    [filteredPayrollData],
  );
  const totalNetSalary = React.useMemo(
    () => filteredPayrollData.reduce((sum, e) => sum + (e.netSalary || 0), 0),
    [filteredPayrollData],
  );
  const totalDeductions = React.useMemo(
    () =>
      filteredPayrollData.reduce((sum, e) => sum + (e.totalDeductions || 0), 0),
    [filteredPayrollData],
  );

  // salaryStats using real payrollData counts but filtered totals
  const salaryStats = React.useMemo(
    () => [
      {
        icon: require("lucide-react").DollarSign,
        value: `${totalGrossSalary.toLocaleString()} ${t("currency")}`,
        label: t("totalGross"),
        color: "#0004f1c4",
        subtitle: t("monthlyGross"),
      },
      {
        icon: require("lucide-react").UserCheck,
        value: payrollDataHook.payrollData
          .filter((p) => p.status === "paid")
          .length.toString(),
        label: t("employeesPaid"),
        color: currentTheme.colors.success,
        subtitle: t("currentMonth"),
      },
      {
        icon: require("lucide-react").Clock,
        value: payrollDataHook.payrollData
          .filter((p) => p.status === "pending")
          .length.toString(),
        label: t("pendingPayments"),
        color: currentTheme.colors.warning,
        subtitle: t("awaitingApproval"),
      },
      {
        icon: require("lucide-react").TrendingDown,
        value: `${totalDeductions.toLocaleString()} ${t("currency")}`,
        label: t("averageKPI"),
        color: currentTheme.colors.danger,
        subtitle: t("performanceIndicator"),
      },
    ],
    [
      payrollDataHook.payrollData,
      totalGrossSalary,
      totalDeductions,
      t,
      currentTheme,
    ],
  );

  const {
    handleOpenAllowanceDeductionImport,
    handleAllowanceDeductionFileSelect,
  } = useAllowanceDeductionImport({
    t,
    showAlert,
    refreshData: payrollDataHook.refreshData,
  });

  const { handleExportPayrollExcel, handleExportPayrollPDF } = usePayrollExport(
    {
      t,
      isRTL,
      language,
      selectedMonth: filters.selectedMonth,
      filteredPayrollData,
      payrollData: payrollDataHook.payrollData,
      totalGrossSalary,
      totalNetSalary,
      totalDeductions,
      showAlert,
    },
  );

  // Visibility rules: fetch on mount
  useEffect(() => {
    fetchVisibilityRules();
  }, []);

  // ─── Skeleton فقط في أول تحميل — مش هيظهر تاني مع كل ريفريش ─────────────────
  const isInitialLoading = payrollDataHook.loading || loadingPermissions;
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!isInitialLoading) setPageReady(true);
  }, [isInitialLoading]);

  // ───────────────────── UI state ─────────────────────
  const [showTaxDetails, setShowTaxDetails] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [processStep, setProcessStep] = useState(1);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [importSettings, setImportSettings] = useState({
    updateExisting: false,
    skipErrors: true,
    calculateMissing: true,
    defaultYear: new Date().getFullYear(),
    defaultMonth: new Date().getMonth() + 1,
  });
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [showMedicalInsuranceModal, setShowMedicalInsuranceModal] =
    useState(false);
  const [showBulkUnpayModal, setShowBulkUnpayModal] = useState(false);
  const [showTemplateDetailsModal, setShowTemplateDetailsModal] =
    useState(false);
  const [showImportDetailsModal, setShowImportDetailsModal] = useState(false);
  const [deductionTooltip, setDeductionTooltip] = useState({
    visible: false,
    employeeId: null,
    content: "",
    position: { x: 0, y: 0 },
  });
  const [deductionModal, setDeductionModal] = useState({
    isOpen: false,
    employee: null,
  });
  const [allowanceModal, setAllowanceModal] = useState({
    isOpen: false,
    employee: null,
  });
  // Pagination
  const currentItems = filteredPayrollData.slice(0, filters.visibleItems);
  const hasMore = filters.visibleItems < filteredPayrollData.length;

  const handleShowMore = () => {
    filters.setVisibleItems((prev) => prev + itemsPerPage);
  };

  const hasRowActions = Boolean(
    modulePermissions?.employeeDetails ||
    modulePermissions?.employeeDeductions ||
    modulePermissions?.employeeAllowances || // ← السطر الجديد
    modulePermissions?.ignoreAttendance ||
    (modulePermissions?.unpayPayroll ?? modulePermissions?.processPayroll), // row Unpay button
  );

  const hasActionPanel = Boolean(
    modulePermissions?.processPayroll ||
    (modulePermissions?.unpayPayroll ?? modulePermissions?.processPayroll) ||
    modulePermissions?.dataImport ||
    modulePermissions?.dataExport ||
    modulePermissions?.medicalInsurance,
  );

  // ── registry "view" section gate (Admin View only) ─────────────────────
  // Page-level access is PageGuard's job; this enforces the payroll "view"
  // feature itself, matching the attendance/employees access-denied pattern.
  // `modulePermissions.view` is only defined when an admin level's features
  // are explicitly configured — every legacy/bypass path (admins, managers,
  // company-config fallback) leaves it undefined → allowed.
  const payrollViewType = (localStorage.getItem("managerViewType") || localStorage.getItem("viewType") || "manager").toLowerCase();
  const canViewPayroll = payrollViewType !== "admin" || (modulePermissions?.view ?? true);

  const handleExportAllowanceDeductionTemplate = () => {
    try {
      const templateRows = [
        {
          infinityCode: "1001",
          date: "01/03/2026",
          transportation_allowance: 500,
          variable_allowance: 300,
          overtime_allowance: 250,
          penalty_deduction: 100,
          late_deduction: 25,
          absence_days: 1,
          penalty_days: "",
          sick_leave_25_days: 0.5,
          late_arrival_days: "",
        },
        {
          infinityCode: "1002",
          date: "15/03/2026",
          absence_days: 2,
          penalty_days: 1,
          transportation_allowance: 500,
        },
      ];
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(templateRows);
      XLSX.utils.book_append_sheet(workbook, worksheet, "AllowancesDeductions");
      XLSX.writeFile(workbook, "allowances_deductions_template.xlsx");
    } catch (error) {
      showAlert(t("error"), t("allowanceDeductionImportError"), "error");
    }
  };

  const handleShowTemplateDetails = () => setShowTemplateDetailsModal(true);
  const handleCloseTemplateDetails = () => setShowTemplateDetailsModal(false);
  const handleShowImportDetails = () => setShowImportDetailsModal(true);
  const handleCloseImportDetails = () => setShowImportDetailsModal(false);

  const handleDownloadTemplate = async () => {
    const templateUrl =
      typeof templateFile === "string" ? templateFile : templateFile?.default;
    if (!templateUrl) {
      showAlert(t("error"), "Unable to locate payroll template file.", "error");
      return;
    }
    try {
      const response = await fetch(templateUrl);
      if (!response.ok)
        throw new Error(`Failed to fetch template: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "payroll_template.xlsx";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Template download failed:", error);
      showAlert(
        t("error"),
        t("errorDownloadingTemplate") || "Template download failed.",
        "error",
      );
    }
  };

  const handleDownloadAllTemplates = () => {
    handleDownloadTemplate();
    handleExportAllowanceDeductionTemplate();
    setShowTemplateDetailsModal(false);
  };

  // Modal body scroll lock
  useEffect(() => {
    const anyModalOpen =
      showProcessModal ||
      showImportModal ||
      selectedEmployee ||
      showTemplateDetailsModal ||
      showImportDetailsModal ||
      deductionModal?.isOpen === true ||
      allowanceModal?.isOpen === true; // ← السطر الجديد

    if (anyModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "visible";
      document.body.style.paddingRight = "0px";
    }
    return () => {
      document.body.style.overflow = "visible";
      document.body.style.paddingRight = "0px";
    };
  }, [showProcessModal, showImportModal, selectedEmployee, deductionModal]);

  const getResponsiveStyles = () => ({
    header: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      borderRadius:
        screenSize === "xs" ? "1rem" : screenSize === "sm" ? "1.25rem" : "1rem",
      padding: spacing.padding,
      marginTop: -40,
      marginBottom: spacing.margin,
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
          : "0 0.5rem 2rem rgba(0,0,0,0.1)",
      backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
    },
    container: {
      marginTop: -20,
      direction: language === "ar" ? "rtl" : "ltr",
    },
    statCard: {
      background: theme === "dark" ? "transparent" : "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.5rem",
      padding: spacing.padding,
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.05)",
      boxShadow:
        theme === "dark"
          ? "0 8px 32px rgba(0,0,0,0.3)"
          : "0 8px 32px rgba(0,0,0,0.1)",
      cursor: "pointer",
    },
    welcomeSection: {},
    welcomeTitle: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: "28px",
      fontWeight: "700",
      textShadow:
        theme === "dark"
          ? "0 0.125rem 0.25rem rgba(0,0,0,0.3)"
          : "0 0.125rem 0.25rem rgba(0,0,0,0.1)",
    },
    primaryBtn: {
      background: "#2e7cba",
      color: "white",
      border: "none",
      padding: screenSize === "xs" ? "0.55rem 0.9rem" : "0.65rem 1.1rem",
      borderRadius: screenSize === "xs" ? "0.65rem" : "0.85rem",
      fontSize: "0.92rem",
      fontWeight: "700",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "0.4rem",
      boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.24)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    secondaryBtn: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
      color: theme === "dark" ? "white" : "#1a1a1a",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.2)"
          : "1px solid rgba(0,0,0,0.1)",
      padding: screenSize === "xs" ? "0.55rem 0.9rem" : "0.65rem 1.1rem",
      borderRadius: screenSize === "xs" ? "0.65rem" : "0.85rem",
      fontSize: "0.92rem",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "0.4rem",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    filterContainer: {
      display: "grid",
      gridTemplateColumns:
        screenSize === "xs"
          ? "1fr"
          : screenSize === "sm"
            ? "repeat(2, 1fr)"
            : screenSize === "md"
              ? "repeat(3, 1fr)"
              : "repeat(4, 1fr)",
      gap: spacing.gap,
      marginBottom: spacing.margin,
      padding: spacing.padding,
      background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.5)",
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.05)",
    },
    filterGroup: {
      display: "flex",
      flexDirection: "column",
      gap: spacing.small,
    },
    filterLabel: {
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.9)"
          : "rgba(30, 41, 59, 0.85)",
      fontSize: fonts.small,
      fontWeight: "500",
      marginBottom: spacing.xs,
    },
    filterInput: {
      width: "100%",
      padding: screenSize === "xs" ? "0.5rem 0.75rem" : "0.625rem 0.875rem",
      fontSize: fonts.body,
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.75rem",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(148, 163, 184, 0.2)",
      background:
        theme === "dark" ? "rgba(30, 41, 59, 0.4)" : "rgba(255, 255, 255, 0.8)",
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.95)"
          : "rgba(30, 41, 59, 0.9)",
      outline: "none",
      transition: "all 0.3s ease",
    },
    tableContainer: {
      overflowX: "auto",
      overflowY: "hidden",
      marginTop: spacing.margin,
      borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
      position: "relative",
      background:
        theme === "dark" ? "rgba(30, 41, 59, 0.4)" : "rgba(255, 255, 255, 0.8)",
      ...(screenSize === "xs" && {
        maxWidth: "100%",
        WebkitOverflowScrolling: "touch",
      }),
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      minWidth:
        screenSize === "xs" ? "600px" : screenSize === "sm" ? "700px" : "900px",
      position: "relative",
      zIndex: 1,
    },
    tableHeader: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      padding:
        screenSize === "xs"
          ? "0.75rem 0.5rem"
          : screenSize === "sm"
            ? "1rem 0.75rem"
            : "1.25rem 1rem",
      textAlign: "center",
      fontSize: fonts.tiny,
      fontWeight: "700",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      borderBottom:
        theme === "dark"
          ? "2px solid rgba(59, 130, 246, 0.3)"
          : "2px solid rgba(59, 130, 246, 0.1)",
      whiteSpace: "nowrap",
      position: "sticky",
      top: 0,
      zIndex: 10,
      backdropFilter: "blur(0.625rem)",
      boxShadow:
        theme === "dark"
          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
      transition: "all 0.3s ease",
    },
    tableCell: {
      padding:
        screenSize === "xs"
          ? "0.75rem 0.5rem"
          : screenSize === "sm"
            ? "1rem 0.75rem"
            : "1.25rem 1rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.06)"
          : "1px solid rgba(148, 163, 184, 0.08)",
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.95)"
          : "rgba(30, 41, 59, 0.9)",
      fontSize: fonts.body,
      fontWeight: "500",
      verticalAlign: isRTL ? "right" : "left",
      justifyContent: isRTL ? "right" : "left",
      textAlign: isRTL ? "right" : "left",
      position: "relative",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      background: "transparent",
    },
  });

  const styles = getResponsiveStyles();

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the first successful load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && isInitialLoading) {
    return (
      <div style={styles.container}>
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل بيانات المرتبات..." : "Loading payroll information..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Header skeleton — styles.header carries a large negative marginTop
            meant to tuck it under the topbar; neutralize it here so it
            doesn't slide up over the LoadingPill above it. */}
        <div style={{ ...styles.header, marginTop: 0, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "24px" }}>
          <div>
            <SkeletonBox width="260px" height="32px" style={{ marginBottom: "12px" }} />
            <SkeletonBox width="340px" height="18px" />
          </div>
          <SkeletonBox width="48px" height="48px" style={{ borderRadius: "12px" }} />
        </div>

        {/* Stats skeleton */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              screenSize === "xs" ? "1fr" : screenSize === "md" ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
            gap: screenSize === "xs" ? "12px" : "20px",
            marginBottom: spacing.margin,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatCard key={i} cardStyle={{ ...styles.statCard, marginBottom: 0 }} />
          ))}
        </div>

        {/* Action buttons skeleton */}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: spacing.margin }}>
          {[1, 2, 3, 4].map((i) => (
            <SkeletonBox key={i} width="150px" height="42px" style={{ borderRadius: "10px" }} />
          ))}
        </div>

        {/* Filters + table skeleton */}
        <div style={styles.statCard}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "12px",
              marginBottom: spacing.margin,
            }}
          >
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonBox key={i} width="100%" height="42px" style={{ borderRadius: "10px" }} />
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <SkeletonBox width="100%" height="44px" style={{ borderRadius: "8px" }} />
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonBox key={i} width="100%" height="56px" style={{ borderRadius: "8px" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── access denied: payroll "view" feature disabled (Admin View only) ──
  if (!loadingPermissions && !canViewPayroll) {
    return (
      <div style={styles.container}>
        <div
          style={{
            ...getCardStyle(theme),
            marginTop: 0,
            padding: "3rem 2rem",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              color: currentTheme.colors.textPrimary,
              fontSize: "1.25rem",
              marginBottom: "0.75rem",
            }}
          >
            {isRTL ? "غير مصرح" : "Access denied"}
          </h2>
          <p style={{ color: currentTheme.colors.textSecondary }}>
            {isRTL
              ? "ليس لديك إذن لعرض صفحة الرواتب."
              : "You do not have permission to view payroll."}
          </p>
        </div>
      </div>
    );
  }

  // ───────────────────────── Render ─────────────────────────
  return (
    <div>
      <PayrollHeader
        styles={styles} 
        t={t}
        translations={translations}
        language={language}
        theme={theme}
        setShowBenefitsModal={setShowBenefitsModal}
      />

      <PayrollStatsGrid
        salaryStats={salaryStats}
        theme={theme}
        spacing={spacing}
        fonts={fonts}
        screenSize={screenSize}
        styles={styles}
      />

      {/* <BenefitsModal
        t={t}
        theme={theme}
        currentTheme={currentTheme}
        showBenefitsModal={showBenefitsModal}
        setShowBenefitsModal={setShowBenefitsModal}
      /> */}

      {/* ── Unified Layout (same as EmployeeTable_Final) ──────────── */}
      <div style={{ marginTop: "24px", display: "flex", flexDirection: "column", gap: "24px", marginBottom: "24px" }}>
        {/* Filter Card */}
        <div style={{ ...getCardStyle(theme), marginTop: "0px", marginBottom: "0px" }}>
          <PayrollFilters
            t={t}
            styles={styles}
            currentTheme={currentTheme}
            theme={theme}
            language={language}
            payrollData={payrollDataHook.payrollData}
            searchTerm={filters.searchTerm}
            setSearchTerm={filters.setSearchTerm}
            statusFilter={filters.statusFilter}
            setStatusFilter={filters.setStatusFilter}
            statusSearch={filters.statusSearch}
            setStatusSearch={filters.setStatusSearch}
            selectedDepartment={filters.selectedDepartment}
            setSelectedDepartment={filters.setSelectedDepartment}
            departmentSearch={filters.departmentSearch}
            setDepartmentSearch={filters.setDepartmentSearch}
            selectedCompany={filters.selectedCompany}
            setSelectedCompany={filters.setSelectedCompany}
            companySearch={filters.companySearch}
            setCompanySearch={filters.setCompanySearch}
            selectedMonth={filters.selectedMonth}
            setSelectedMonth={filters.setSelectedMonth}
            yearSearch={filters.yearSearch}
            setYearSearch={filters.setYearSearch}
            monthSearch={filters.monthSearch}
            setMonthSearch={filters.setMonthSearch}
            filteredCount={filteredPayrollData.length}
            totalCount={payrollDataHook.payrollData?.length || 0}
            actionButtons={
              hasActionPanel ? (
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    flexWrap: "wrap",
                    paddingTop: "16px",
                    borderTop:
                      theme === "dark"
                        ? "1px solid rgba(255,255,255,0.1)"
                        : "1px solid rgba(0,0,0,0.1)",
                    flexDirection: isRTL ? "row" : "row",
                  }}
                >
                  <input
                    id="allowance-deduction-import-input"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: "none" }}
                    onChange={handleAllowanceDeductionFileSelect}
                  />
                  {modulePermissions?.processPayroll && (
                    <button
                      onClick={() => setShowProcessModal(true)}
                      style={{
                        ...getTopBarButtonStyle(theme, false),
                        flexDirection: isRTL ? "row-reverse" : "row",
                        background: "#2e7cba",
                        color: "#fff",
                        border: "none",
                      }}
                    >
                      <CreditCard size={16} />
                      {t("processPayroll")}
                    </button>
                  )}
                  {(modulePermissions?.unpayPayroll ?? modulePermissions?.processPayroll) && (
                    <button
                      onClick={() => setShowBulkUnpayModal(true)}
                      style={{
                        ...getTopBarButtonStyle(theme, false),
                        flexDirection: isRTL ? "row-reverse" : "row",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
                      }}
                    >
                      <RotateCcw size={16} />
                      {t("bulkUnpay")}
                    </button>
                  )}
                  {modulePermissions?.dataImport && (
                    <button
                      onClick={handleShowImportDetails}
                      style={{
                        ...getTopBarButtonStyle(theme, false),
                        flexDirection: isRTL ? "row-reverse" : "row",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
                      }}
                    >
                      <Upload size={16} />
                      {t("import")}
                    </button>
                  )}
                  {(modulePermissions?.template ?? modulePermissions?.dataExport) && (
                    <button
                      onClick={handleShowTemplateDetails}
                      style={{
                        ...getTopBarButtonStyle(theme, false),
                        flexDirection: isRTL ? "row-reverse" : "row",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
                      }}
                    >
                      <Download size={16} />
                      {t("templateButton")}
                    </button>
                  )}
                  {filteredPayrollData && filteredPayrollData.length > 0 && (modulePermissions?.exportPayrollPDF ?? modulePermissions?.dataExport) && (
                    <>
                      <button
                        onClick={handleExportPayrollPDF}
                        style={{
                          ...getTopBarButtonStyle(theme, false),
                          flexDirection: isRTL ? "row-reverse" : "row",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
                        }}
                      >
                        <FileText size={16} />
                        {t("exportPayrollPDF")}
                      </button>
                    </>
                  )}
                  {filteredPayrollData && filteredPayrollData.length > 0 && (modulePermissions?.exportPayrollExcel ?? modulePermissions?.dataExport) && (
                    <>
                      <button
                        onClick={() => handleExportPayrollExcel(filteredPayrollData)}
                        style={{
                          ...getTopBarButtonStyle(theme, false),
                          flexDirection: isRTL ? "row-reverse" : "row",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
                        }}
                      >
                        <Download size={16} />
                        {t("exportPayrollExcel")}
                      </button>
                    </>
                  )}
                  {modulePermissions?.medicalInsurance && (
                    <button
                      onClick={() => setShowMedicalInsuranceModal(true)}
                      style={{
                        ...getTopBarButtonStyle(theme, false),
                        flexDirection: isRTL ? "row-reverse" : "row",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
                      }}
                    >
                      <Heart size={16} />
                      {t("medicalInsurance")}
                    </button>
                  )}
                </div>
              ) : null
            }
          />
        </div>

        {/* Table Card */}
        <div style={{ ...getCardStyle(theme), marginTop: "0px" }}>
          <PayrollTable
            t={t}
            styles={styles}
            theme={theme}
            language={language}
            currentTheme={currentTheme}
            fonts={fonts}
            spacing={spacing}
            screenSize={screenSize}
            currentItems={currentItems}
            hasMore={hasMore}
            visibleItems={filters.visibleItems}
            filteredPayrollData={filteredPayrollData}
            togglingIgnoredIds={payrollDataHook.togglingIgnoredIds}
            unpayingIds={payrollDataHook.unpayingIds}
            setAllowanceModal={setAllowanceModal}
            modulePermissions={modulePermissions}
            hasRowActions={hasRowActions}
            profile={profile}
            setSelectedEmployee={setSelectedEmployee}
            setDeductionModal={setDeductionModal}
            handleToggleIsIgnored={payrollDataHook.handleToggleIsIgnored}
            handleUnpayPayroll={payrollDataHook.handleUnpayPayroll}
            handleShowMore={handleShowMore}
            setDeductionTooltip={setDeductionTooltip}
            showAlert={showAlert}
          />
        </div>
      </div>

      {selectedEmployee && (
        <EmployeeDetailsModal
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
          currentTheme={currentTheme}
          t={t}
          language={language}
          theme={theme}
          isRTL={isRTL}
          showAlert={showAlert}
        />
      )}

      {showProcessModal && (
        <ProcessPayrollModal
          onClose={() => {
            setShowProcessModal(false);
            setProcessStep(1);
            setSelectedEmployees([]);
          }}
          onProcessComplete={() => {
            payrollDataHook.refreshPayrollData();
            setShowProcessModal(false);
            setProcessStep(1);
            setSelectedEmployees([]);
          }}
          payrollData={payrollDataHook.payrollData}
          selectedEmployees={selectedEmployees}
          setSelectedEmployees={setSelectedEmployees}
          selectedMonth={filters.selectedMonth}
          setSelectedMonth={filters.setSelectedMonth}
          theme={theme}
          language={language}
          API_BASE_URL={API_BASE_URL}
          clientName={clientName}
          showAlert={showAlert}
          processStep={processStep}
          setProcessStep={setProcessStep}
        />
      )}

      {showBulkUnpayModal && (
        <BulkUnpayModal
          isOpen={showBulkUnpayModal}
          onClose={() => setShowBulkUnpayModal(false)}
          payrollData={payrollDataHook.payrollData}
          currentTheme={currentTheme}
          t={t}
          onUnpayComplete={() => payrollDataHook.refreshPayrollData()}
          showAlert={showAlert}
          apiFetch={apiFetch}
          API_BASE_URL={API_BASE_URL}
        />
      )}

      {deductionModal.isOpen && (
        <DeductionsModal
          isOpen={deductionModal.isOpen}
          employee={deductionModal.employee}
          onClose={() => setDeductionModal({ isOpen: false, employee: null })}
          API_BASE_URL={API_BASE_URL}
          currentTheme={currentTheme}
          t={t}
          language={language}
          theme={theme}
          isRTL={isRTL}
          showAlert={showAlert}
        />
      )}
      {allowanceModal.isOpen && (
        <AllowancesModal
          isOpen={allowanceModal.isOpen}
          employee={allowanceModal.employee}
          onClose={() => setAllowanceModal({ isOpen: false, employee: null })}
          API_BASE_URL={API_BASE_URL}
          currentTheme={currentTheme}
          t={t}
          language={language}
          theme={theme}
          isRTL={isRTL}
          showAlert={showAlert}
          screenSize={screenSize}
        />
      )}

      {showImportModal && (
        <ExcelImportModal
          onClose={() => {
            setShowImportModal(false);
            setImportStep(1);
            setImportFile(null);
            setImportData(null);
          }}
          onImportComplete={() => {
            payrollDataHook.refreshPayrollData();
            setShowImportModal(false);
          }}
          API_BASE_URL={API_BASE_URL}
          t={t}
          language={language}
          theme={theme}
          isRTL={isRTL}
          importStep={importStep}
          setImportStep={setImportStep}
          importFile={importFile}
          setImportFile={setImportFile}
          importData={importData}
          setImportData={setImportData}
          importLoading={importLoading}
          setImportLoading={setImportLoading}
          importError={importError}
          setImportError={setImportError}
          importProgress={importProgress}
          setImportProgress={setImportProgress}
          importResults={importResults}
          setImportResults={setImportResults}
          importSettings={importSettings}
          setImportSettings={setImportSettings}
        />
      )}

      {showMedicalInsuranceModal && (
        <MedicalInsuranceModal
          onClose={() => setShowMedicalInsuranceModal(false)}
          API_BASE_URL={API_BASE_URL}
          clientName={clientName}
          workingCompany={workingCompany}
          theme={theme}
          language={language}
        />
      )}

      {showTemplateDetailsModal && (
        <div
          className="modal-overlay"
          onClick={handleCloseTemplateDetails}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99990,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(690px, 100%)",
              height: "min(370px, 100%)",
              borderRadius: 16,
              background: currentTheme.colors.cardBackground,
              color: currentTheme.colors.textPrimary,
              boxShadow: currentTheme.shadows.card,
              overflow: "hidden",
              border: `1px solid ${currentTheme.colors.borderLight}`,
            }}
          >
            <div
              style={{
                padding: "20px",
                borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>
                  {t("templateButton")}
                </div>
                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "0.92rem",
                    color: currentTheme.colors.textSecondary,
                  }}
                >
                  {t("templateButtonTitle")}
                </div>
              </div>
              <button
                onClick={handleCloseTemplateDetails}
                style={{
                  background: "transparent",
                  border: "none",
                  color: currentTheme.colors.textMuted,
                  fontSize: "2rem",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                padding: "20px",
                fontSize: "0.95rem",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
              }}
            >
              {`- File name: payroll_template.xlsx\n- Required columns: Infinity Code, Employee Name, Payroll Start/End dates, Working days, Absence Days, OverTime Days, Bonus Value, expenses, Achievement, and more.\n\n- If you need the allowance/deduction file, use the allowance_deduction_template.xlsx format.`}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "nowrap",
                gap: "10px",
                padding: "60px 25px",
                borderTop: `1px solid ${currentTheme.colors.borderLight}`,
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleCloseTemplateDetails}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: `1px solid ${currentTheme.colors.borderLight}`,
                  background:
                    theme === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : currentTheme.colors.inputBackground,
                  color: currentTheme.colors.textPrimary,
                  cursor: "pointer",
                }}
              >
                {language === "ar" ? "إغلاق" : "Close"}
              </button>
              <button
                onClick={() => {
                  handleDownloadTemplate();
                  setShowTemplateDetailsModal(false);
                }}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "none",
                  background: currentTheme.colors.primary,
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                {language === "ar"
                  ? "تحميل النموذج"
                  : "Download Payroll Template"}
              </button>
              <button
                onClick={() => {
                  handleExportAllowanceDeductionTemplate();
                  setShowTemplateDetailsModal(false);
                }}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "none",
                  background: currentTheme.colors.success,
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                {language === "ar"
                  ? "تحميل نموذج البدلات/الخصومات"
                  : "Download A/D Template"}
              </button>
              <button
                onClick={handleDownloadAllTemplates}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "none",
                  background: "#7c3aed",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                {language === "ar" ? "تحميل الكل" : "Download All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportDetailsModal && (
        <div
          className="modal-overlay"
          onClick={handleCloseImportDetails}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99990,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(690px, 100%)",
              height: "min(350px, 100%)",
              borderRadius: 16,
              background: currentTheme.colors.cardBackground,
              color: currentTheme.colors.textPrimary,
              boxShadow: currentTheme.shadows.card,
              overflow: "hidden",
              border: `1px solid ${currentTheme.colors.borderLight}`,
            }}
          >
            <div
              style={{
                padding: "20px",
                borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>
                  {t("import")}
                </div>
                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "0.92rem",
                    color: currentTheme.colors.textSecondary,
                  }}
                >
                  {t("importOptions")}
                </div>
              </div>
              <button
                onClick={handleCloseImportDetails}
                style={{
                  background: "transparent",
                  border: "none",
                  color: currentTheme.colors.textMuted,
                  fontSize: "2rem",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                padding: "20px",
                fontSize: "0.95rem",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
              }}
            >
              {t("formatDescription")}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "nowrap",
                gap: "10px",
                padding: "60px 25px",
                borderTop: `1px solid ${currentTheme.colors.borderLight}`,
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleCloseImportDetails}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: `1px solid ${currentTheme.colors.borderLight}`,
                  background:
                    theme === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : currentTheme.colors.inputBackground,
                  color: currentTheme.colors.textPrimary,
                  cursor: "pointer",
                }}
              >
                {language === "ar" ? "إغلاق" : "Close"}
              </button>
              <button
                onClick={() => {
                  setShowImportModal(true);
                  setShowImportDetailsModal(false);
                }}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "none",
                  background: currentTheme.colors.primary,
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                {t("importExcel")}
              </button>
              <button
                onClick={() => {
                  handleOpenAllowanceDeductionImport();
                  setShowImportDetailsModal(false);
                }}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "none",
                  background: currentTheme.colors.success,
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                {t("importAllowancesDeductions")}
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        showCancel={alertModal.showCancel}
        onConfirm={closeAlert}
        onClose={closeAlert}
        theme={theme}
        language={language}
      />

      <style>{`
        * { box-sizing: border-box; }
        html { scroll-behavior: smooth; overflow-x: clip; overflow-y: auto; }
        body { overflow-x: clip; overflow-y: auto !important; min-height: 100vh; margin: 0; padding: 0; }
        .modal-container { position: fixed; top: 0; left: 0; right: 0; bottom: 0; overflow-y: auto; -webkit-overflow-scrolling: touch; }
        .modal-overlay { backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
        .modal-close-btn { width: 36px; height: 36px; border-radius: 9999px; display: inline-flex; align-items: center; justify-content: center; transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .modal-close-btn:hover { transform: scale(1.06); box-shadow: 0 8px 20px rgba(2,6,23,0.24); }
      `}</style>
    </div>
  );
};

export default ProfessionalPayrollSystem;
